import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FiSearch } from 'react-icons/fi';
import { SearchService } from '../../services/SearchService';
import './KnowledgeHub.css';

const CATEGORIES = ['All', 'Food', 'Farming', 'Bio Pest', 'Organic', 'Soil Science'];
const BLOGS_PER_PAGE = 9;

function BlogCard({ blog, onBlogClick }) {
  return (
    <div className="kb-blog-card" onClick={() => onBlogClick && onBlogClick(blog)}>
      <div className="kb-card-image-wrap">
        <img
          src={blog.image || blog.image_url}
          alt={blog.title}
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=800&auto=format&fit=crop&q=80'; }}
        />
        <div className="kb-card-category-badge">{blog.category}</div>
        <div className="kb-card-date-badge">
          <span className="kb-date-day">{blog.date}</span>
          <span className="kb-date-month">{blog.month}</span>
        </div>
      </div>
      <div className="kb-card-body">
        <h3 className="kb-card-title">{blog.title}</h3>
        <p className="kb-card-summary">{blog.summary}</p>
        <div className="kb-card-footer">
          <span className="kb-card-author">✍️ {blog.author || 'Admin'}</span>
          {blog.commentsCount !== undefined && (
            <span className="kb-card-comments">💬 {blog.commentsCount} comments</span>
          )}
          <span className="kb-read-more">Read More →</span>
        </div>
      </div>
    </div>
  );
}

export default function KnowledgeHub({ onBlogClick, onBack }) {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('search');
    if (q !== null) {
      setSearchQuery(q);
    }
  }, [location.search]);

  useEffect(() => {
    setLoading(true);
    SearchService.getAllBlogs().then(data => {
      // Normalize blog data
      const normalized = data.map(b => ({
        ...b,
        image: b.image || b.image_url || 'https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=800&auto=format&fit=crop&q=80',
        date: b.date || (b.created_at ? new Date(b.created_at).getDate().toString() : '01'),
        month: b.month || (b.created_at ? ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][new Date(b.created_at).getMonth()] : 'JAN'),
        author: b.author || (b.first_name ? `${b.first_name} ${b.last_name}` : 'Admin'),
        commentsCount: b.commentsCount !== undefined ? b.commentsCount : (b.comments ? b.comments.length : 0),
      }));
      setBlogs(normalized);
      setLoading(false);
    });
  }, []);

  const filtered = blogs.filter(b => {
    const matchCat = activeCategory === 'All' || b.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || b.title.toLowerCase().includes(q) || (b.summary || '').toLowerCase().includes(q) || (b.category || '').toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  const totalPages = Math.ceil(filtered.length / BLOGS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * BLOGS_PER_PAGE, currentPage * BLOGS_PER_PAGE);

  const featuredBlog = blogs[0] || null;

  return (
    <div className="kb-page">
      {/* Hero Banner */}
      <div className="kb-hero">
        <div className="kb-hero-overlay">
          <div className="container kb-hero-inner">

            <h1 className="kb-hero-title">Knowledge Hub</h1>
            <p className="kb-hero-subtitle">
              Expert insights on organic farming, soil science, bio-fertilizers, and sustainable agriculture
            </p>
            {/* Search bar */}
            <div className="kb-search-wrap">
              <input
                type="text"
                placeholder="Search articles, topics, categories..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              />
              <button className="kb-search-btn" aria-label="Search">
                <FiSearch size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Blog (first blog) */}
      {!loading && featuredBlog && !searchQuery && activeCategory === 'All' && (
        <section className="kb-featured-section">
          <div className="container">
            <div className="kb-section-label">📌 Featured Article</div>
            <div className="kb-featured-card" onClick={() => onBlogClick && onBlogClick(featuredBlog)}>
              <div className="kb-featured-image">
                <img
                  src={featuredBlog.image}
                  alt={featuredBlog.title}
                  onError={e => { e.target.src = 'https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=800&auto=format&fit=crop&q=80'; }}
                />
                <div className="kb-featured-cat-badge">{featuredBlog.category}</div>
              </div>
              <div className="kb-featured-content">
                <div className="kb-featured-meta">
                  <span>✍️ {featuredBlog.author}</span>
                  <span>📅 {featuredBlog.date} {featuredBlog.month}</span>
                  {featuredBlog.commentsCount !== undefined && (
                    <span>💬 {featuredBlog.commentsCount} comments</span>
                  )}
                </div>
                <h2 className="kb-featured-title">{featuredBlog.title}</h2>
                <p className="kb-featured-summary">{featuredBlog.summary}</p>
                <button className="kb-featured-read-btn">Read Full Article →</button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Category Tabs */}
      <section className="kb-categories-section">
        <div className="container">
          <div className="kb-category-tabs">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`kb-cat-tab ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => { setActiveCategory(cat); setCurrentPage(1); }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="kb-grid-section">
        <div className="container">
          {/* Results info */}
          <div className="kb-results-info">
            {loading ? 'Loading articles...' : `Showing ${paginated.length} of ${filtered.length} articles`}
            {searchQuery && <strong> for "{searchQuery}"</strong>}
          </div>

          {loading ? (
            <div className="kb-loading">
              <div className="kb-spinner"></div>
              <p>Loading articles...</p>
            </div>
          ) : paginated.length === 0 ? (
            <div className="kb-empty">
              <div className="kb-empty-icon">📚</div>
              <h3>No articles found</h3>
              <p>Try adjusting your search or category filter</p>
              <button onClick={() => { setSearchQuery(''); setActiveCategory('All'); setCurrentPage(1); }}>
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="kb-grid">
              {paginated.map(blog => (
                <BlogCard key={blog.id} blog={blog} onBlogClick={onBlogClick} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="kb-pagination">
              <button
                className={`kb-page-btn ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ‹ Prev
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i + 1}
                  className={`kb-page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className={`kb-page-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next ›
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}

    </div>
  );
}
